import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Concierge } from "@/components/Concierge";
import Home from "@/pages/Home";
import HowWeWork from "@/pages/HowWeWork";
import SuburbIQ from "@/pages/SuburbIQ";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Contact from "@/pages/Contact";
import About from "@/pages/About";
import Insights from "@/pages/Insights";
import InsightArticle from "@/pages/InsightArticle";
import Faqs from "@/pages/Faqs";
import Fees from "@/pages/Fees";
import ServicePage from "@/pages/ServicePage";
import PartnerConfirmed from "@/pages/PartnerConfirmed";
import { BROCHURE_FILE_URL } from "@/data/content";

// Stable /brochure URL (approved 3 Nov 2026): instantly hands off to the
// versioned static PDF so the same link survives future brochure updates.
// Includes a JS-off fallback link. Non-JS HTTP clients can use
// /api/brochure, which issues a proper 307 redirect to the same file.
const BrochureRedirect = () => {
  useEffect(() => {
    window.location.replace(BROCHURE_FILE_URL);
  }, []);
  return (
    <main className="bg-herowhite">
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <p className="font-serif text-2xl text-charcoal" data-testid="brochure-redirect-note">
          Opening the 80 Metrics brochure…
        </p>
        <a
          href={BROCHURE_FILE_URL}
          data-testid="brochure-redirect-fallback"
          className="mt-6 inline-block text-base font-semibold text-golddark underline underline-offset-4"
        >
          Download our brochure (PDF · 20 pages · approx. 8.6 MB)
        </a>
      </div>
    </main>
  );
};
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Unsubscribe from "@/pages/Unsubscribe";

const LenisRoot = ({ children }) => {
  const lenisRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    // Respect reduced-motion: no smooth-scroll hijacking, native scrolling stays.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      lenisRef.current = null;
      return undefined;
    }
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        if (lenisRef.current) {
          setTimeout(() => lenisRef.current?.scrollTo(el, { offset: -96 }), 60);
        } else {
          el.scrollIntoView();
        }
        return;
      }
    }
    lenisRef.current?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  return children;
};

const RedirectWithQuery = ({ to }) => {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search }} replace />;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LenisRoot>
          <Nav />
          <main className="pt-20 lg:pt-24">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/how-we-work" element={<HowWeWork />} />
              <Route path="/suburbiq" element={<SuburbIQ />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/about" element={<About />} />
              <Route path="/insights" element={<Insights />} />
              <Route path="/insights/:slug" element={<InsightArticle />} />
              <Route path="/faqs" element={<Faqs />} />
              <Route path="/fees" element={<Fees />} />
              <Route path="/buyer-advocacy" element={<ServicePage serviceKey="buyer-advocacy" />} />
              <Route path="/vendor-advocacy" element={<ServicePage serviceKey="vendor-advocacy" />} />
              <Route path="/property-portfolio-assessment" element={<ServicePage serviceKey="property-portfolio-review" />} />
              {/* Legacy route: SPA compatibility redirect preserving query strings.
                  A server-side 301 at the host is the launch follow-up (LAUNCH_STATUS.md). */}
              <Route path="/property-portfolio-review" element={<RedirectWithQuery to="/property-portfolio-assessment" />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              {/* Legacy indexed routes from the previous site (client-side redirect;
                  server-side 301s at the CDN/host are recorded in LAUNCH_STATUS.md) */}
              <Route path="/about-us" element={<Navigate to="/about" replace />} />
              {/* Legacy direct PDF URLs — files are capture-gated now; send
                  visitors to the matching guide card instead of a 404. */}
              <Route path="/assets/resources/buyer-purchase-checklist.pdf" element={<Navigate to="/buyer-advocacy#guides" replace />} />
              <Route path="/assets/resources/smart-sellers-playbook.pdf" element={<Navigate to="/vendor-advocacy#guides" replace />} />
              <Route path="/assets/resources/style-with-strategy.pdf" element={<Navigate to="/vendor-advocacy#guides" replace />} />
              <Route path="/assets/resources/first-home-school-suburb-web-guide.pdf" element={<Navigate to="/insights#guides" replace />} />
              <Route path="/assets/resources/elite-school-suburbs.pdf" element={<Navigate to="/insights#guides" replace />} />
              <Route path="/brochure" element={<BrochureRedirect />} />
              <Route path="/suburbiq/partner-confirmed" element={<PartnerConfirmed />} />
              <Route path="*" element={<Home />} />
            </Routes>
          </main>
          <Footer />
          <Concierge />
          <Toaster position="bottom-left" toastOptions={{ style: { borderRadius: 0, background: "#1A1A1A", color: "#FAF9F6", border: "1px solid rgba(255,255,255,0.15)" } }} />
        </LenisRoot>
      </BrowserRouter>
    </div>
  );
}

export default App;
